import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { getAdminDashboardAnalyticsQuery } from "../../Graphql/querries/adminAnalyticsQuery";
import type {
  AdminCompanyBusinessTypeMetric,
  AdminPaymentBusinessTypeMetric,
  AdminSubscriptionBusinessTypeMetric,
} from "../../Graphql/types/adminAnalytics";

export interface AdminAnalyticsInput {
  dateFrom?: string | null;
  dateTo?: string | null;
  countryCode?: string | null;
  businessType?: string | null;
}

export interface AdminDashboardAnalyticsResponse {
  getAdminDashboardAnalytics: {
    code: string;
    companyAnalytics?: {
      totalCompanies: number;
      activeCompanies: number;
      registeredCompanies: number;
      pendingCompanies: number;
      deactivatedCompanies: number;
      companiesRegisteredInPeriod: number;
      companiesByStatus?: { key: string; value: number }[];
      byBusinessType?: AdminCompanyBusinessTypeMetric[] | null;
    } | null;
    subscriptionAnalytics?: {
      totalSubscriptions: number;
      activeSubscriptions: number;
      subscriptionsCreatedInPeriod: number;
      subscriptionsExpiringInPeriod: number;
      subscriptionsByStatus?: { key: string; value: number }[];
      byBusinessType?: AdminSubscriptionBusinessTypeMetric[] | null;
    } | null;
    paymentAnalytics?: {
      totalRevenue: number;
      revenueInPeriod: number;
      vatToPay: number;
      vatInPeriod: number;
      totalPaymentsCount: number;
      mostUsedPaymentMethod?: string | null;
      paymentsByMethod?: { key: string; value: number }[];
      paymentsByStatus?: { key: string; value: number }[];
      revenueByMethod?: { key: string; value: number }[];
      byBusinessType?: AdminPaymentBusinessTypeMetric[] | null;
    } | null;
    superAdminAnalytics?: {
      totalSuperAdmins: number;
      superAdminsByRole?: { key: string; value: number }[];
      activeSuperAdmins: number;
      superAdminsByStatus?: { key: string; value: number }[];
      superAdminsLastLoginInPeriod: number;
      newSuperAdminsInPeriod: number;
    } | null;
  };
}

export const useAdminDashboardAnalytics = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    AdminDashboardAnalyticsResponse,
    Error,
    AdminAnalyticsInput | void
  >({
    mutationKey: ["getAdminDashboardAnalytics"],
    mutationFn: (input) =>
      fetchGraphQL(
        getAdminDashboardAnalyticsQuery,
        { input: input ?? {} },
        setAlert,
      ),
  });
};
